import { SeededRng, type RandomSeed } from "./rng";
import {
  type AnswerChoice,
  type ChallengeBand,
  type ChoiceId,
  type ComparisonSymbol,
  type MathAnswer,
  type MathQuestion,
  type MathSkill,
  type MathTier,
  type QuestionRequest,
  type VisualAid,
} from "./types";

interface QuestionDraft {
  readonly promptHebrew: string;
  readonly spokenHebrew: string;
  readonly expressionLTR: string;
  readonly correctAnswer: MathAnswer;
  readonly distractors?: readonly MathAnswer[];
  readonly minChoice?: number;
  readonly maxChoice?: number;
  readonly visualAid?: VisualAid;
  readonly signatureDetail?: string;
}

const CHOICE_IDS = ["choice-1", "choice-2", "choice-3"] as const;
const COMPARISON_CHOICES: readonly ComparisonSymbol[] = ["<", "=", ">"];

function answerKey(value: MathAnswer): string {
  return `${typeof value}:${value}`;
}

function answerLabel(value: MathAnswer): string {
  return String(value);
}

function numericChoices(
  correct: number,
  distractors: readonly MathAnswer[],
  min: number,
  max: number,
  rng: SeededRng,
): readonly [AnswerChoice, AnswerChoice, AnswerChoice] {
  const values = new Set<number>([correct]);
  for (const distractor of distractors) {
    if (
      typeof distractor === "number" &&
      Number.isInteger(distractor) &&
      distractor >= min &&
      distractor <= max
    ) {
      values.add(distractor);
    }
    if (values.size === 3) break;
  }

  const offsets = rng.shuffle([1, -1, 2, -2, 3, -3, 5, -5, 10, -10]);
  for (const offset of offsets) {
    const candidate = correct + offset;
    if (candidate >= min && candidate <= max) values.add(candidate);
    if (values.size === 3) break;
  }

  for (let candidate = min; values.size < 3 && candidate <= max; candidate += 1) {
    values.add(candidate);
  }

  if (values.size < 3) {
    throw new RangeError(`Choice range ${min}..${max} cannot contain three answers.`);
  }

  const selected = rng.shuffle([...values].slice(0, 3));
  return selected.map((value, index) => ({
    id: CHOICE_IDS[index] as ChoiceId,
    value,
    label: answerLabel(value),
  })) as [AnswerChoice, AnswerChoice, AnswerChoice];
}

function comparisonChoices(rng: SeededRng): readonly [AnswerChoice, AnswerChoice, AnswerChoice] {
  return rng.shuffle(COMPARISON_CHOICES).map((value, index) => ({
    id: CHOICE_IDS[index] as ChoiceId,
    value,
    label: value,
  })) as [AnswerChoice, AnswerChoice, AnswerChoice];
}

function createChoices(draft: QuestionDraft, rng: SeededRng) {
  if (typeof draft.correctAnswer !== "number") return comparisonChoices(rng);
  return numericChoices(
    draft.correctAnswer,
    draft.distractors ?? [],
    draft.minChoice ?? 0,
    draft.maxChoice ?? 20,
    rng,
  );
}

function arrangementForTier(tier: MathTier): "line" | "rows" | "scattered" {
  if (tier <= 2) return "line";
  if (tier <= 4) return "rows";
  return "scattered";
}

const FEMININE_NUMBERS = ["אפס", "אחת", "שתיים", "שלוש", "ארבע", "חמש", "שש", "שבע", "שמונה", "תשע"] as const;

function feminineCount(count: number, singular: string, plural: string): string {
  if (count === 0) return `אפס ${plural}`;
  if (count === 1) return `${singular} אחת`;
  if (count === 2) return `שתי ${plural}`;
  if (count < FEMININE_NUMBERS.length) return `${FEMININE_NUMBERS[count]} ${plural}`;
  return `${count} ${plural}`;
}

function groupsOfTen(count: number): string {
  return feminineCount(count, "קבוצה", "קבוצות") + " של עשר";
}

function units(count: number): string {
  return feminineCount(count, "יחידה", "יחידות");
}

function stars(count: number): string {
  return count === 1 ? "כוכב אחד" : `${count} כוכבים`;
}

function countingQuestion(tier: MathTier, rng: SeededRng): QuestionDraft {
  const ranges: Readonly<Record<MathTier, readonly [number, number]>> = {
    1: [3, 7],
    2: [6, 10],
    3: [9, 13],
    4: [12, 16],
    5: [15, 19],
    6: [17, 20],
  };
  const [min, max] = ranges[tier];
  const count = rng.integer(min, max);
  const icon = rng.boolean() ? "gem" : "star";
  return {
    promptHebrew: icon === "gem" ? "כמה יהלומים יש?" : "כמה כוכבים יש?",
    spokenHebrew: icon === "gem" ? "כמה יהלומים את רואה?" : "כמה כוכבים את רואה?",
    expressionLTR: "?",
    correctAnswer: count,
    distractors: [count - 1, count + 1, count - 2, count + 2],
    minChoice: 0,
    maxChoice: 20,
    visualAid: {
      kind: "objects",
      count,
      icon,
      arrangement: arrangementForTier(tier),
    },
    signatureDetail: String(count),
  };
}

function sequenceQuestion(tier: MathTier, rng: SeededRng, supportMode: boolean): QuestionDraft {
  let step = 1;
  let direction: 1 | -1 = 1;
  let lower = 0;
  let upper = 10;

  if (tier === 1) upper = 7;
  if (tier === 2) {
    lower = 2;
    upper = 12;
  }
  if (tier === 3) {
    lower = 4;
    upper = 20;
    direction = rng.boolean(0.25) ? -1 : 1;
  }
  if (tier === 4) {
    upper = 20;
    direction = -1;
  }
  if (tier === 5) {
    upper = 20;
    step = rng.pick([2, 5]);
    direction = rng.boolean() ? -1 : 1;
  }
  if (tier === 6) {
    upper = 100;
    step = 10;
    lower = 10;
    direction = rng.boolean() ? -1 : 1;
  }

  const signedStep = step * direction;
  const minStart = direction === 1 ? lower : lower + step * 3;
  const maxStart = direction === 1 ? upper - step * 3 : upper;
  const start = rng.integer(minStart, maxStart);
  const values = [start, start + signedStep, start + signedStep * 2, start + signedStep * 3];
  const missingIndex = tier <= 2 ? rng.integer(1, 3) : rng.integer(0, 3);
  const correct = values[missingIndex] as number;
  const expressionLTR = values
    .map((value, index) => (index === missingIndex ? "?" : String(value)))
    .join(", ");

  return {
    promptHebrew: "איזה מספר חסר ברצף?",
    spokenHebrew: `הקשיבי לרצף. ${values.map((value, index) => index === missingIndex ? "חסר" : String(value)).join(", ")}. איזה מספר חסר?`,
    expressionLTR,
    correctAnswer: correct,
    distractors: [correct - signedStep, correct + signedStep, correct - 1, correct + 1],
    minChoice: 0,
    maxChoice: upper,
    visualAid: supportMode
      ? { kind: "number-line", min: lower, max: upper, highlighted: values }
      : undefined,
    signatureDetail: `${signedStep}:${expressionLTR}`,
  };
}

function additionOperands(tier: MathTier, rng: SeededRng): readonly [number, number, number] {
  const ranges: Readonly<Record<MathTier, readonly [number, number, number]>> = {
    1: [4, 8, 1],
    2: [6, 10, 2],
    3: [9, 14, 3],
    4: [12, 17, 4],
    5: [14, 20, 5],
    6: [16, 20, 6],
  };
  const [minTotal, maxTotal, minOperand] = ranges[tier];
  const total = rng.integer(minTotal, maxTotal);
  const first = rng.integer(minOperand, total - minOperand);
  return [first, total - first, total];
}

function additionQuestion(tier: MathTier, rng: SeededRng, supportMode: boolean): QuestionDraft {
  const [first, second, total] = additionOperands(tier, rng);
  return {
    promptHebrew: "כמה יש ביחד?",
    spokenHebrew: `בואי נחבר. כמה זה ${first} ועוד ${second}?`,
    expressionLTR: `${first} + ${second} = ?`,
    correctAnswer: total,
    distractors: [total - 1, total + 1, Math.abs(first - second), first, second],
    minChoice: 0,
    maxChoice: 20,
    visualAid:
      tier <= 2 || supportMode
        ? { kind: "object-groups", groups: [first, second], icon: "gem" }
        : undefined,
  };
}

function subtractionOperands(tier: MathTier, rng: SeededRng): readonly [number, number, number] {
  const ranges: Readonly<Record<MathTier, readonly [number, number, number, number]>> = {
    1: [5, 8, 1, 1],
    2: [7, 12, 2, 2],
    3: [10, 15, 3, 3],
    4: [13, 18, 4, 3],
    5: [16, 20, 5, 4],
    6: [18, 20, 6, 5],
  };
  const [minStart, maxStart, minRemoved, minResult] = ranges[tier];
  const start = rng.integer(minStart, maxStart);
  const removed = rng.integer(minRemoved, start - minResult);
  return [start, removed, start - removed];
}

function subtractionQuestion(tier: MathTier, rng: SeededRng, supportMode: boolean): QuestionDraft {
  const [start, removed, result] = subtractionOperands(tier, rng);
  return {
    promptHebrew: "כמה נשארו?",
    spokenHebrew: `היו ${stars(start)}. ${removed === 1 ? "כוכב אחד נעלם" : `${stars(removed)} נעלמו`}. כמה נשארו?`,
    expressionLTR: `${start} - ${removed} = ?`,
    correctAnswer: result,
    distractors: [result - 1, result + 1, start + removed, removed],
    minChoice: 0,
    maxChoice: 20,
    visualAid:
      tier <= 2 || supportMode
        ? { kind: "object-removal", start, removed, icon: "star" }
        : undefined,
  };
}

function missingNumberQuestion(tier: MathTier, rng: SeededRng, supportMode: boolean): QuestionDraft {
  const useAddition = tier <= 2 || (tier >= 4 && rng.boolean());
  if (useAddition) {
    const sourceTier = Math.min(tier, 6) as MathTier;
    const [first, second, total] = additionOperands(sourceTier, rng);
    const hideFirst = rng.boolean();
    const correct = hideFirst ? first : second;
    return {
      promptHebrew: "איזה מספר חסר?",
      spokenHebrew: hideFirst
        ? `איזה מספר ועוד ${second} שווה ${total}?`
        : `${first} ועוד איזה מספר שווה ${total}?`,
      expressionLTR: hideFirst ? `? + ${second} = ${total}` : `${first} + ? = ${total}`,
      correctAnswer: correct,
      distractors: [correct - 1, correct + 1, total, Math.abs(first - second)],
      minChoice: 0,
      maxChoice: 20,
      visualAid: supportMode
        ? { kind: "object-groups", groups: [first, second], icon: "gem" }
        : undefined,
    };
  }

  const subtractionTier = tier === 3 ? 3 : tier;
  const [start, removed, result] = subtractionOperands(subtractionTier, rng);
  const hideStart = tier >= 5 && rng.boolean(0.35);
  const correct = hideStart ? start : removed;
  return {
    promptHebrew: "איזה מספר חסר?",
    spokenHebrew: hideStart
      ? `איזה מספר פחות ${removed} שווה ${result}?`
      : `${start} פחות איזה מספר שווה ${result}?`,
    expressionLTR: hideStart ? `? - ${removed} = ${result}` : `${start} - ? = ${result}`,
    correctAnswer: correct,
    distractors: [correct - 1, correct + 1, result, start],
    minChoice: 0,
    maxChoice: 20,
    visualAid: supportMode
      ? { kind: "object-removal", start, removed, icon: "star" }
      : undefined,
  };
}

function compareSymbol(left: number, right: number): ComparisonSymbol {
  if (left < right) return "<";
  if (left > right) return ">";
  return "=";
}

function comparisonQuestion(tier: MathTier, rng: SeededRng, supportMode: boolean): QuestionDraft {
  const simpleRanges: Readonly<Record<MathTier, readonly [number, number]>> = {
    1: [2, 8],
    2: [4, 12],
    3: [6, 16],
    4: [8, 20],
    5: [8, 20],
    6: [8, 20],
  };
  const [min, max] = simpleRanges[tier];
  let left = rng.integer(min, max);
  let right = rng.boolean(0.25) ? left : rng.integer(min, max);

  if (left === right && !rng.boolean(0.5)) {
    right = right === max ? right - 1 : right + 1;
  }

  if (tier === 5) {
    const leftFirst = rng.integer(2, left - 2);
    const rightFirst = rng.integer(2, right - 2);
    return {
      promptHebrew: "איזה סימן מתאים?",
      spokenHebrew: `מצד אחד, ${leftFirst} ועוד ${left - leftFirst}. מצד שני, ${rightFirst} ועוד ${right - rightFirst}. איזה סימן מתאים ביניהם?`,
      expressionLTR: `${leftFirst} + ${left - leftFirst} ? ${rightFirst} + ${right - rightFirst}`,
      correctAnswer: compareSymbol(left, right),
      visualAid: supportMode
        ? { kind: "number-line", min: 0, max: 20, highlighted: [left, right] }
        : undefined,
      signatureDetail: `${leftFirst}+${left - leftFirst}:${rightFirst}+${right - rightFirst}`,
    };
  }

  if (tier === 6) {
    left = rng.integer(6, 16);
    right = rng.boolean(0.25) ? left : rng.integer(6, 16);
    const leftFirst = rng.integer(2, left - 2);
    const removed = rng.integer(2, Math.min(8, 20 - right));
    const rightStart = right + removed;
    return {
      promptHebrew: "איזה סימן מתאים בין התרגילים?",
      spokenHebrew: `מצד אחד, ${leftFirst} ועוד ${left - leftFirst}. מצד שני, ${rightStart} פחות ${removed}. איזה סימן מתאים ביניהם?`,
      expressionLTR: `${leftFirst} + ${left - leftFirst} ? ${rightStart} - ${removed}`,
      correctAnswer: compareSymbol(left, right),
      visualAid: supportMode
        ? { kind: "number-line", min: 0, max: 20, highlighted: [left, right] }
        : undefined,
      signatureDetail: `${leftFirst}+${left - leftFirst}:${rightStart}-${removed}`,
    };
  }

  return {
    promptHebrew: "איזה סימן מתאים?",
    spokenHebrew: `איזה סימן מתאים בין ${left} לבין ${right}?`,
    expressionLTR: `${left} ? ${right}`,
    correctAnswer: compareSymbol(left, right),
    visualAid:
      tier <= 2 || supportMode
        ? { kind: "number-line", min: 0, max, highlighted: [left, right] }
        : undefined,
  };
}

function tensQuestion(tier: MathTier, rng: SeededRng, supportMode: boolean): QuestionDraft {
  if (tier === 1) {
    const tens = rng.integer(1, 2);
    const ones = rng.integer(0, 9);
    const number = tens * 10 + ones;
    return {
      promptHebrew: "איזה מספר בנוי מהעשרות והיחידות?",
      spokenHebrew: `יש כאן ${groupsOfTen(tens)}, וגם ${units(ones)}. איזה מספר זה?`,
      expressionLTR: `${tens * 10} + ${ones} = ?`,
      correctAnswer: number,
      distractors: [tens + ones * 10, number - 1, number + 1],
      minChoice: 0,
      maxChoice: 100,
      visualAid: { kind: "base-ten", tens, ones },
    };
  }

  if (tier === 2) {
    const tens = rng.integer(1, 5);
    return {
      promptHebrew: "כמה עשרות יש במספר?",
      spokenHebrew: `במספר ${tens * 10}, כמה קבוצות של עשר יש?`,
      expressionLTR: `${tens * 10}`,
      correctAnswer: tens,
      distractors: [tens - 1, tens + 1, tens * 10],
      minChoice: 0,
      maxChoice: 10,
      visualAid: { kind: "base-ten", tens, ones: 0 },
    };
  }

  if (tier === 4) {
    const tens = rng.integer(1, 7);
    const ones = rng.integer(0, 9);
    const number = tens * 10 + ones;
    return {
      promptHebrew: "כמה יחידות יש במספר?",
      spokenHebrew: `במספר ${number}, כמה יחידות בודדות יש?`,
      expressionLTR: `${number}`,
      correctAnswer: ones,
      distractors: [ones - 1, ones + 1, tens],
      minChoice: 0,
      maxChoice: 10,
      visualAid: { kind: "base-ten", tens, ones },
    };
  }

  if (tier === 6) {
    const addTen = rng.boolean();
    const tens = addTen ? rng.integer(1, 8) : rng.integer(2, 9);
    const ones = rng.integer(0, 9);
    const number = tens * 10 + ones;
    const answer = number + (addTen ? 10 : -10);
    return {
      promptHebrew: addTen ? "איזה מספר גדול בעשר?" : "איזה מספר קטן בעשר?",
      spokenHebrew: addTen
        ? `איזה מספר גדול בעשר מן המספר ${number}?`
        : `איזה מספר קטן בעשר מן המספר ${number}?`,
      expressionLTR: `${number} ${addTen ? "+" : "-"} 10 = ?`,
      correctAnswer: answer,
      distractors: [number, answer - 1, answer + 1, number + (addTen ? 20 : -20)],
      minChoice: 0,
      maxChoice: 100,
      visualAid: supportMode
        ? { kind: "base-ten", tens: Math.floor(answer / 10), ones: answer % 10 }
        : undefined,
    };
  }

  const maxTens = tier === 3 ? 5 : 9;
  const tens = rng.integer(1, maxTens);
  const ones = rng.integer(0, 9);
  const number = tens * 10 + ones;
  return {
    promptHebrew: "איזה מספר מתאים?",
    spokenHebrew: tier === 5 && !supportMode
      ? `כמה זה ${tens * 10} ועוד ${ones}?`
      : `יש כאן ${groupsOfTen(tens)}, וגם ${units(ones)}. איזה מספר זה?`,
    expressionLTR: `${tens * 10} + ${ones} = ?`,
    correctAnswer: number,
    distractors: [tens + ones * 10, number - 10, number + 10, number - 1, number + 1],
    minChoice: 0,
    maxChoice: 100,
    visualAid: tier <= 4 || supportMode ? { kind: "base-ten", tens, ones } : undefined,
  };
}

const WORD_PROBLEM_NAMES = ["דנה", "נועה", "תמר", "רוני", "מיה"] as const;
const WORD_PROBLEM_ITEMS = ["תפוחים", "בלונים", "פרחים", "מדבקות", "עוגיות"] as const;

/**
 * Spoken story problems. Every actor is feminine so the Hebrew agreement in
 * the templates stays uniform, numbers stay as digits (matching the rest of
 * the game's narration), and each story ends with the actual question.
 */
function wordProblemQuestion(tier: MathTier, rng: SeededRng, supportMode: boolean): QuestionDraft {
  const name = rng.pick(WORD_PROBLEM_NAMES);
  const item = rng.pick(WORD_PROBLEM_ITEMS);
  const kind = tier <= 2
    ? "addition"
    : tier <= 4
      ? (rng.boolean() ? "addition" : "subtraction")
      : (rng.boolean() ? "missing-addend" : "subtraction");

  if (kind === "addition") {
    const [first, second, total] = additionOperands(tier, rng);
    const template = rng.integer(0, 1);
    const story = template === 0
      ? `ל${name} יש ${first} ${item}. היא קיבלה עוד ${second}. כמה ${item} יש לה עכשיו?`
      : `ל${name} יש ${first} ${item} בסל. אמא נתנה לה עוד ${second}. כמה ${item} יש לה בסך הכול?`;
    return {
      promptHebrew: story,
      spokenHebrew: story,
      expressionLTR: tier <= 2 || supportMode ? `${first} + ${second} = ?` : "?",
      correctAnswer: total,
      distractors: [total - 1, total + 1, first, second],
      minChoice: 0,
      maxChoice: 20,
      visualAid: tier <= 2 || supportMode
        ? { kind: "number-line", min: 0, max: 20, highlighted: [first, total] }
        : undefined,
      signatureDetail: `add:${template}:${name}:${item}:${first}+${second}`,
    };
  }

  if (kind === "subtraction") {
    const [start, removed, result] = subtractionOperands(tier, rng);
    const template = rng.integer(0, 2);
    const story = template === 0
      ? `ל${name} היו ${start} ${item}. היא נתנה לחברה ${removed}. כמה ${item} נשארו לה?`
      : template === 1
        ? `ל${name} היו ${start} בלונים. ${removed} בלונים עפו ברוח. כמה בלונים נשארו?`
        : `ל${name} היו ${start} עוגיות. היא אכלה ${removed}. כמה עוגיות נשארו?`;
    return {
      promptHebrew: story,
      spokenHebrew: story,
      expressionLTR: supportMode ? `${start} - ${removed} = ?` : "?",
      correctAnswer: result,
      distractors: [result - 1, result + 1, start, removed],
      minChoice: 0,
      maxChoice: 20,
      visualAid: supportMode
        ? { kind: "number-line", min: 0, max: 20, highlighted: [result, start] }
        : undefined,
      signatureDetail: `sub:${template}:${name}:${start}-${removed}`,
    };
  }

  const [first, second, total] = additionOperands(tier, rng);
  const story = `ל${name} יש ${first} ${item}. כמה עוד היא צריכה לאסוף כדי שיהיו לה ${total}?`;
  return {
    promptHebrew: story,
    spokenHebrew: story,
    expressionLTR: supportMode ? `${first} + ? = ${total}` : "?",
    correctAnswer: second,
    distractors: [second - 1, second + 1, total, first],
    minChoice: 0,
    maxChoice: 20,
    visualAid: supportMode
      ? { kind: "number-line", min: 0, max: 20, highlighted: [first, total] }
      : undefined,
    signatureDetail: `missing:${name}:${item}:${first}?${total}`,
  };
}

function buildDraft(request: QuestionRequest, rng: SeededRng): QuestionDraft {
  const supportMode = request.supportMode ?? false;
  switch (request.skill) {
    case "counting":
      return countingQuestion(request.tier, rng);
    case "sequence":
      return sequenceQuestion(request.tier, rng, supportMode);
    case "addition":
      return additionQuestion(request.tier, rng, supportMode);
    case "subtraction":
      return subtractionQuestion(request.tier, rng, supportMode);
    case "missing-number":
      return missingNumberQuestion(request.tier, rng, supportMode);
    case "compare":
      return comparisonQuestion(request.tier, rng, supportMode);
    case "tens":
      return tensQuestion(request.tier, rng, supportMode);
    case "word-problems":
      return wordProblemQuestion(request.tier, rng, supportMode);
  }
}

export function generateMathQuestion(
  request: QuestionRequest,
  rng: SeededRng,
  ordinal = 0,
): MathQuestion {
  const draft = buildDraft(request, rng);
  const choices = createChoices(draft, rng);
  const matchingChoices = choices.filter(
    (choice) => answerKey(choice.value) === answerKey(draft.correctAnswer),
  );
  if (matchingChoices.length !== 1) {
    throw new Error("A question must have exactly one correct answer choice.");
  }

  const signature = [
    request.skill,
    request.tier,
    draft.expressionLTR,
    answerKey(draft.correctAnswer),
    draft.signatureDetail ?? "",
  ].join("|");

  return {
    id: `math-${ordinal}-${request.skill}-${request.tier}`,
    signature,
    skill: request.skill,
    tier: request.tier,
    challengeBand: request.challengeBand ?? "current",
    supportMode: request.supportMode ?? false,
    promptHebrew: draft.promptHebrew,
    spokenHebrew: draft.spokenHebrew,
    expressionLTR: draft.expressionLTR,
    ...(draft.visualAid === undefined ? {} : { visualAid: draft.visualAid }),
    choices,
    correctAnswer: draft.correctAnswer,
    correctChoiceId: matchingChoices[0]?.id as ChoiceId,
  };
}

export class MathQuestionGenerator {
  private readonly rng: SeededRng;
  private ordinal = 0;
  private previousSignature: string | undefined;

  public constructor(seed: RandomSeed) {
    this.rng = new SeededRng(seed);
  }

  /** Generates a question and guarantees that its content is not repeated immediately. */
  public next(request: QuestionRequest): MathQuestion {
    for (let attempt = 0; attempt < 128; attempt += 1) {
      const question = generateMathQuestion(request, this.rng, this.ordinal);
      if (question.signature !== this.previousSignature) {
        this.previousSignature = question.signature;
        this.ordinal += 1;
        return question;
      }
    }
    throw new Error(`Unable to avoid a repeated ${request.skill} question.`);
  }
}

export function challengeBandForRoll(roll: number): ChallengeBand {
  if (roll < 0 || roll >= 1) throw new RangeError("Challenge roll must be in [0, 1). ");
  if (roll < 0.15) return "review";
  if (roll < 0.75) return "current";
  return "stretch";
}
